import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useCastleSoundManager } from "../components/games/math-castle-defense/SoundManager";
import {
  BREACH_DISTANCE,
  MAX_CASTLE_HEALTH,
  START_DISTANCE,
  TOTAL_WAVES,
  approachRateForWave,
  generateCastleProblem,
  healthAfterCorrectAnswer,
  monsterForWave,
  pointsForAnswer,
} from "../components/games/math-castle-defense/gameUtils";
import BackToGamesButton from "../components/games/BackToGamesButton";
import ChallengeBanner, { readChallengeTarget } from "../components/games/ChallengeBanner";
import { StarIcon, CrownIcon, FlameIcon, BoltIcon } from "../components/games/ui-icons";
import "./MathCastleDefensePage.css";

const PRAISE = ["Direct hit!", "Boom!", "Nice math!", "Kapow!", "You got it!", "Bullseye!"];

const ASSET_BASE = "/assets/games/math-castle-defense";
// Painted character art by monster kind (mirrors iOS MathCastleArt).
const MONSTER_ART = {
  slime: "mc_gloop",
  cyclops: "mc_blinky",
  mushroom: "mc_mushboom",
  dragon: "mc_sparky",
};

// ── Vector fallback art (used if the painted PNG/JPG fails to load) ──────────

const CastleArtSVG = ({ health }) => (
  <svg viewBox="0 0 340 360" role="img">
    <defs>
      <linearGradient id="castleStone" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#FFF8DD" />
        <stop offset="0.5" stopColor="#F0D398" />
        <stop offset="1" stopColor="#C98E4F" />
      </linearGradient>
      <linearGradient id="castleRoof" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#9378F2" />
        <stop offset="0.55" stopColor="#6246C7" />
        <stop offset="1" stopColor="#3B257E" />
      </linearGradient>
      <linearGradient id="castleDoor" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#765342" />
        <stop offset="1" stopColor="#3C2930" />
      </linearGradient>
      <radialGradient id="windowGlow">
        <stop offset="0" stopColor="#FFF8A8" />
        <stop offset="0.62" stopColor="#69D9FF" />
        <stop offset="1" stopColor="#2879B8" />
      </radialGradient>
      <pattern id="castleBricks" width="38" height="22" patternUnits="userSpaceOnUse">
        <path d="M0 21.5h38M19 0v11M0 11h38" fill="none" stroke="#98683E" strokeWidth="1.4" opacity=".24" />
      </pattern>
      <filter id="castleGlow" x="-80%" y="-80%" width="260%" height="260%">
        <feGaussianBlur stdDeviation="5" result="blur" />
        <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
      </filter>
    </defs>
    <ellipse cx="170" cy="337" rx="151" ry="18" fill="#294E34" opacity=".3" />
    <ellipse cx="170" cy="324" rx="135" ry="15" fill="#8BD269" />

    <path d="M56 151h77v174H56zM207 151h77v174h-77z" fill="url(#castleStone)" stroke="#8C5C38" strokeWidth="6" />
    <path d="M103 113h134v212H103z" fill="url(#castleStone)" stroke="#8C5C38" strokeWidth="6" />
    <path d="M73 205h194v120H73z" fill="url(#castleStone)" stroke="#8C5C38" strokeWidth="6" />

    <path d="M56 151h77v174H56zM207 151h77v174h-77zM103 113h134v212H103zM73 205h194v120H73z" fill="url(#castleBricks)" />

    <path d="M47 153v-35h19v18h19v-18h19v18h19v-18h19v35zM198 153v-35h19v18h19v-18h19v18h19v-18h19v35z" fill="#F7DEAA" stroke="#8C5C38" strokeWidth="6" strokeLinejoin="round" />
    <path d="M94 115V78h20v19h22V78h22v19h23V78h22v19h22V78h21v37z" fill="#F7DEAA" stroke="#8C5C38" strokeWidth="6" strokeLinejoin="round" />

    <path d="M42 119L94 31l53 88zM191 119l54-88 53 88z" fill="url(#castleRoof)" stroke="#382475" strokeWidth="6" strokeLinejoin="round" />
    <path d="M109 79L170 2l61 77z" fill="url(#castleRoof)" stroke="#382475" strokeWidth="6" strokeLinejoin="round" />
    <path d="M60 89l34-58 35 58M213 88l32-56 34 56M134 65l36-46 36 46" fill="none" stroke="#B9A7FF" strokeWidth="4" opacity=".75" />

    <path d="M94 33V5l42 14-42 14M170 5v-28l48 15-48 15M245 33V7l40 13-40 13" fill="#FFD24D" stroke="#A56A16" strokeWidth="4" strokeLinejoin="round" />
    <path d="M170-20l29 9-29 9" fill="#FF7183" stroke="#A43B51" strokeWidth="3" />

    <g fill="url(#windowGlow)" stroke="#604333" strokeWidth="5">
      <path d="M72 178v-23c0-25 44-25 44 0v23z" />
      <path d="M224 178v-23c0-25 44-25 44 0v23z" />
      <path d="M146 136v-22c0-27 48-27 48 0v22z" />
    </g>
    <g stroke="#FFF7C3" strokeWidth="3" opacity=".85"><path d="M94 139v39M75 161h38M246 139v39M227 161h38M170 94v42M148 116h44" /></g>

    <path d="M132 325v-70c0-51 76-51 76 0v70z" fill="url(#castleDoor)" stroke="#34252B" strokeWidth="7" />
    <path d="M170 229v96M143 267h54" stroke="#A77B59" strokeWidth="4" />
    <path d="M137 258h66" stroke="#D2AE78" strokeWidth="3" strokeDasharray="5 7" />
    <circle cx="188" cy="279" r="4" fill="#FFD455" />

    <path d="M132 183h76v51h-76z" fill="#FFF5D1" stroke="#8C5C38" strokeWidth="5" />
    <path d="M170 188l9 18 20 3-14 14 3 20-18-10-18 10 3-20-14-14 20-3z" fill="#FFD34F" stroke="#B77816" strokeWidth="3" />
    <path d="M121 198h-25v31h25M219 198h25v31h-25" fill="none" stroke="#8C5C38" strokeWidth="5" />

    <g filter="url(#castleGlow)">
      <circle cx="278" cy="187" r="13" fill="#E9FCFF" stroke="#4BAFE0" strokeWidth="4" />
      <path d="M278 171v32M262 187h32" stroke="#FFF" strokeWidth="4" strokeLinecap="round" />
    </g>

    <path d="M38 313c10-24 27-27 42-6 11-20 28-17 34 8M226 315c9-23 25-25 38-7 10-21 30-18 38 7" fill="#5BAA55" stroke="#34743D" strokeWidth="5" strokeLinecap="round" />
    <circle cx="61" cy="302" r="5" fill="#FFF39B" /><circle cx="277" cy="301" r="5" fill="#FF9FB4" />

    {health <= 2 && <path d="M225 242l-13 16 15 13-18 20" fill="none" stroke="#80543A" strokeWidth="5" strokeLinecap="round" />}
    {health <= 1 && <path d="M87 218l12 15-10 16 14 12" fill="none" stroke="#80543A" strokeWidth="5" strokeLinecap="round" />}
  </svg>
);

const MonsterArtSVG = ({ monster }) => {
  const { kind, color } = monster;
  const outline =
    { slime: "#3C8A3A", cyclops: "#59389A", mushroom: "#9A4A3F", dragon: "#1F8077" }[kind] || "#3C8A3A";

  const eye = (cx, cy, r = 9) => (
    <>
      <ellipse cx={cx} cy={cy} rx={r} ry={r + 1.5} fill="#fff" />
      <circle cx={cx + 1} cy={cy + 2} r={r * 0.52} fill="#2A2540" />
      <circle cx={cx - 2.4} cy={cy - 2} r={r * 0.26} fill="#fff" />
      <circle cx={cx + 3} cy={cy + 4} r={r * 0.13} fill="#fff" opacity=".85" />
    </>
  );

  const cheeks = (cx1, cx2, cy) => (
    <>
      <ellipse cx={cx1} cy={cy} rx="7" ry="4.6" fill="#FF93A8" opacity=".55" />
      <ellipse cx={cx2} cy={cy} rx="7" ry="4.6" fill="#FF93A8" opacity=".55" />
    </>
  );

  return (
    <svg viewBox="0 0 160 160">
      <defs>
        <radialGradient id="mcdShine" cx="34%" cy="26%" r="72%">
          <stop offset="0" stopColor="#fff" stopOpacity=".6" />
          <stop offset="52%" stopColor="#fff" stopOpacity=".12" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="mcdBelly" cx="50%" cy="40%" r="62%">
          <stop offset="0" stopColor="#fff" stopOpacity=".78" />
          <stop offset="100%" stopColor="#fff" stopOpacity=".26" />
        </radialGradient>
        <linearGradient id="mcdShade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="58%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity=".16" />
        </linearGradient>
      </defs>

      <ellipse cx="80" cy="151" rx="50" ry="10" fill="#244625" opacity=".22" />

      {kind === "slime" && (
        <>
          <ellipse cx="57" cy="141" rx="14" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <ellipse cx="103" cy="141" rx="14" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <path d="M80 45q3-15 8-21" fill="none" stroke={outline} strokeWidth="4" strokeLinecap="round" />
          <circle cx="90" cy="21" r="7.5" fill={color} stroke={outline} strokeWidth="4" />
          <path d="M26 96c0-31 22-54 54-54s54 23 54 54c0 9-3 16-8 22 5 6 4 15-3 19-19 11-67 11-86 0-7-4-8-13-3-19-5-6-8-13-8-22z" fill={color} stroke={outline} strokeWidth="5" strokeLinejoin="round" />
          <path d="M26 96c0-31 22-54 54-54s54 23 54 54c0 9-3 16-8 22 5 6 4 15-3 19-19 11-67 11-86 0-7-4-8-13-3-19-5-6-8-13-8-22z" fill="url(#mcdShade)" />
          <ellipse cx="62" cy="74" rx="40" ry="38" fill="url(#mcdShine)" />
          <circle cx="56" cy="66" r="6.5" fill="#fff" opacity=".85" />
          {cheeks(56, 104, 96)}
          {eye(64, 80)}
          {eye(100, 80)}
          <path d="M70 97q10 11 20 0" fill="none" stroke={outline} strokeWidth="5" strokeLinecap="round" />
        </>
      )}

      {kind === "cyclops" && (
        <>
          <ellipse cx="58" cy="142" rx="13" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <ellipse cx="102" cy="142" rx="13" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <path d="M52 44l-8-22 22 14z" fill={color} stroke={outline} strokeWidth="4" strokeLinejoin="round" />
          <path d="M108 44l8-22-22 14z" fill={color} stroke={outline} strokeWidth="4" strokeLinejoin="round" />
          <path d="M30 122c-9-32 0-77 50-81 50 4 59 49 50 81-20 14-80 14-100 0z" fill={color} stroke={outline} strokeWidth="5" strokeLinejoin="round" />
          <path d="M30 122c-9-32 0-77 50-81 50 4 59 49 50 81-20 14-80 14-100 0z" fill="url(#mcdShade)" />
          <path d="M28 96q-15 5-15 22M132 96q15 5 15 22" fill="none" stroke={outline} strokeWidth="8" strokeLinecap="round" />
          <ellipse cx="62" cy="70" rx="38" ry="34" fill="url(#mcdShine)" />
          <ellipse cx="80" cy="80" rx="26" ry="24" fill="#fff" stroke={outline} strokeWidth="5" />
          <circle cx="82" cy="83" r="12" fill="#2A2540" />
          <circle cx="75" cy="75" r="5" fill="#fff" />
          <circle cx="87" cy="89" r="2.6" fill="#fff" opacity=".85" />
          {cheeks(50, 110, 106)}
          <path d="M66 118q14 10 28 0" fill="none" stroke={outline} strokeWidth="5" strokeLinecap="round" />
        </>
      )}

      {kind === "mushroom" && (
        <>
          <ellipse cx="60" cy="144" rx="12" ry="8" fill="#FFE9D0" stroke={outline} strokeWidth="4" />
          <ellipse cx="100" cy="144" rx="12" ry="8" fill="#FFE9D0" stroke={outline} strokeWidth="4" />
          <path d="M48 74c-3 28 2 52 32 52s35-24 32-52z" fill="#FFE9D0" stroke={outline} strokeWidth="5" strokeLinejoin="round" />
          <path d="M16 76c2-44 126-44 128 0-34 17-94 17-128 0z" fill={color} stroke={outline} strokeWidth="5" strokeLinejoin="round" />
          <circle cx="48" cy="54" r="11" fill="#FFF1E2" />
          <circle cx="99" cy="60" r="8" fill="#FFF1E2" />
          <circle cx="76" cy="42" r="6" fill="#FFF1E2" />
          <ellipse cx="52" cy="48" rx="32" ry="16" fill="url(#mcdShine)" />
          {cheeks(56, 104, 100)}
          {eye(64, 94, 8)}
          {eye(96, 94, 8)}
          <path d="M72 107q8 9 16 0" fill="none" stroke={outline} strokeWidth="5" strokeLinecap="round" />
        </>
      )}

      {kind === "dragon" && (
        <>
          <ellipse cx="60" cy="142" rx="13" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <ellipse cx="100" cy="142" rx="13" ry="9" fill={color} stroke={outline} strokeWidth="4" />
          <path d="M42 82C16 70 8 44 8 44c24-2 38 12 44 26z" fill="#BFEFE6" stroke={outline} strokeWidth="4" strokeLinejoin="round" />
          <path d="M118 82c26-12 34-38 34-38-24-2-38 12-44 26z" fill="#BFEFE6" stroke={outline} strokeWidth="4" strokeLinejoin="round" />
          <path d="M80 40l-9 8 9-24 9 24z" fill={color} stroke={outline} strokeWidth="4" strokeLinejoin="round" />
          <path d="M36 124c-10-34 0-77 44-81 44 4 54 47 44 81-18 14-74 14-88 0z" fill={color} stroke={outline} strokeWidth="5" strokeLinejoin="round" />
          <path d="M36 124c-10-34 0-77 44-81 44 4 54 47 44 81-18 14-74 14-88 0z" fill="url(#mcdShade)" />
          <path d="M58 96c0-14 44-14 44 0v18c0 12-44 12-44 0z" fill="url(#mcdBelly)" stroke={outline} strokeWidth="3" opacity=".92" />
          <path d="M62 101h36M62 112h36" stroke={outline} strokeWidth="2.5" opacity=".4" />
          <ellipse cx="60" cy="74" rx="34" ry="30" fill="url(#mcdShine)" />
          {cheeks(54, 106, 96)}
          {eye(64, 80)}
          {eye(98, 80)}
          <path d="M71 95q10 8 20 0" fill="none" stroke={outline} strokeWidth="5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
};

// ── Painted-or-vector figures (prefer painted, fall back to SVG on error) ────

const CastleFigure = ({ health, shaking }) => {
  const [failed, setFailed] = useState(false);
  const showDamage = health <= 2;

  if (failed) {
    return (
      <div className={`mcd-castle ${shaking ? "is-shaking" : ""}`} aria-hidden="true">
        <CastleArtSVG health={health} />
      </div>
    );
  }

  return (
    <div className={`mcd-castle mcd-castle-painted ${shaking ? "is-shaking" : ""}`} aria-hidden="true">
      <img
        src={`${ASSET_BASE}/mc_castle.png`}
        alt=""
        draggable="false"
        className={showDamage ? "is-faded" : ""}
        onError={() => setFailed(true)}
      />
      <img
        src={`${ASSET_BASE}/mc_castle_damaged.png`}
        alt=""
        draggable="false"
        className={showDamage ? "" : "is-faded"}
      />
    </div>
  );
};

const MonsterFigure = ({ monster, hit, defeated }) => {
  const src = MONSTER_ART[monster.kind];
  const [failed, setFailed] = useState(false);

  // Retry the painted art whenever the monster kind changes.
  useEffect(() => setFailed(false), [src]);

  const className = `mcd-monster-art ${hit ? "is-hit" : ""} ${defeated ? "is-defeated" : ""}`;

  if (failed || !src) {
    return (
      <div className={className} aria-hidden="true">
        <MonsterArtSVG monster={monster} />
      </div>
    );
  }

  return (
    <div className={`${className} is-painted`} aria-hidden="true">
      <img src={`${ASSET_BASE}/${src}.png`} alt="" draggable="false" onError={() => setFailed(true)} />
    </div>
  );
};

const HealthPips = ({ health, maxHealth, kind = "monster" }) => (
  <div className={`mcd-health-pips is-${kind}`} aria-label={`${health} of ${maxHealth} health`}>
    {Array.from({ length: maxHealth }).map((_, index) => (
      <span key={index} className={index < health ? "is-full" : ""}>
        {kind === "castle" ? "♥" : ""}
      </span>
    ))}
  </div>
);

const Clouds = () => (
  <div className="mcd-clouds" aria-hidden="true">
    <i /><i /><i />
  </div>
);

const CreatureLineup = () => (
  <div className="mcd-creature-lineup" aria-hidden="true">
    {[1, 2, 3, 4].map((wave) => {
      const creature = monsterForWave(wave);
      return (
        <div className="mcd-lineup-item" key={creature.kind}>
          <MonsterFigure monster={creature} />
          <span>{creature.name}</span>
        </div>
      );
    })}
  </div>
);

const IntroOverlay = ({ onStart, onBack }) => (
  <div className="mcd-overlay" role="dialog" aria-modal="true" aria-labelledby="mcd-intro-title">
    <div className="mcd-intro-card">
      <button type="button" className="mcd-modal-close" onClick={onBack} aria-label="Back to games">×</button>
      <div className="mcd-intro-emblem" aria-hidden="true"><span>♜</span></div>
      <p className="mcd-eyebrow">The kingdom needs a math hero</p>
      <h1 id="mcd-intro-title">Math Castle Defense</h1>
      <p className="mcd-intro-lead">Solve each problem to blast the creature marching on your gate. Clear all {TOTAL_WAVES} waves — they get faster and trickier as you go!</p>
      <CreatureLineup />
      <div className="mcd-how-row">
        <span><b>1</b> Read the problem</span><i>→</i><span><b>2</b> Tap the answer</span><i>→</i><span><b>3</b> Knock off a heart</span>
      </div>
      <button type="button" className="mcd-primary-button" onClick={onStart} autoFocus>
        <span>Defend the castle</span><b>→</b>
      </button>
    </div>
  </div>
);

const ResultOverlay = ({ won, score, defeated, bestCombo, starsAwarded, onRetry, onBack }) => (
  <div className="mcd-overlay" role="dialog" aria-modal="true" aria-labelledby="mcd-result-title">
    <motion.div
      className={`mcd-result-card ${won ? "is-victory" : "is-gameover"}`}
      initial={{ opacity: 0, y: 24, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 240, damping: 20 }}
    >
      <div className="mcd-result-burst" aria-hidden="true">
        {won ? (
          <CrownIcon size={44} />
        ) : (
          <img
            src="/assets/games/math-castle-defense/mc_castle_damaged.png"
            alt=""
            style={{ width: 52, height: 52, objectFit: "contain" }}
            draggable={false}
          />
        )}
      </div>
      <p className="mcd-eyebrow">{won ? "All waves cleared" : "The creatures got through"}</p>
      <h2 id="mcd-result-title">{won ? "Castle saved!" : "Rebuild & retry!"}</h2>
      <p>{won ? "Your quick thinking kept the kingdom safe." : "Every great defender learns from the next wave."}</p>
      <div className="mcd-result-stats">
        <span><small>Score</small><strong>{score.toLocaleString()}</strong></span>
        <span><small>Creatures stopped</small><strong>{defeated} of {TOTAL_WAVES}</strong></span>
        <span><small>Best streak</small><strong>×{Math.max(bestCombo, 1)}</strong></span>
      </div>
      {starsAwarded > 0 && (
        <div className="mcd-star-badge" aria-label={`${starsAwarded} stars earned`}>
          <StarIcon size={22} /><span>+{starsAwarded} star{starsAwarded === 1 ? "" : "s"} earned</span>
        </div>
      )}
      <button type="button" className="mcd-primary-button" onClick={onRetry}>Play again <b>↻</b></button>
      <button type="button" className="mcd-text-button" onClick={onBack}>Back to all games</button>
    </motion.div>
  </div>
);

const MathCastleDefensePage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const { enabled: soundEnabled, play, toggle: toggleSound } = useCastleSoundManager();

  const [status, setStatus] = useState("intro");
  // A friend's beat-my-score link (?challenge=N) — read once on mount.
  const [challengeTarget] = useState(readChallengeTarget);
  const [wave, setWave] = useState(1);
  const [castleHealth, setCastleHealth] = useState(MAX_CASTLE_HEALTH);
  const [monster, setMonster] = useState(() => monsterForWave(1));
  const [problem, setProblem] = useState(() => generateCastleProblem(1));
  const [distance, setDistance] = useState(START_DISTANCE);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [defeated, setDefeated] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [boltFlight, setBoltFlight] = useState(false);
  const [hitBurst, setHitBurst] = useState(false);
  const [monsterHit, setMonsterHit] = useState(false);
  const [monsterDefeated, setMonsterDefeated] = useState(false);
  const [castleShaking, setCastleShaking] = useState(false);
  const [fieldShaking, setFieldShaking] = useState(false);
  const [breachLunge, setBreachLunge] = useState(false);
  const [entering, setEntering] = useState(false);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [won, setWon] = useState(false);
  const [scorePop, setScorePop] = useState(null);
  const [showWaveBanner, setShowWaveBanner] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [answerFlash, setAnswerFlash] = useState(null);
  const timeoutIdsRef = useRef(new Set());
  const savedRef = useRef(false);

  // Monster is closing in — used to flash a danger warning around the arena.
  const danger = status === "playing" && !resolving && distance <= 46;
  const atOneHeart = status === "playing" && castleHealth === 1;

  const later = useCallback((callback, delay) => {
    const id = window.setTimeout(() => {
      timeoutIdsRef.current.delete(id);
      callback();
    }, delay);
    timeoutIdsRef.current.add(id);
    return id;
  }, []);

  useEffect(
    () => () => {
      timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
      timeoutIdsRef.current.clear();
    },
    []
  );

  // Flash a "Wave N" announcement + hop the creature in whenever a fresh wave
  // begins. The approach timer waits for the entrance to finish.
  useEffect(() => {
    if (status !== "playing") return undefined;
    setShowWaveBanner(true);
    setEntering(true);
    const bannerId = window.setTimeout(() => setShowWaveBanner(false), 1250);
    const enterId = window.setTimeout(() => setEntering(false), 900);
    return () => {
      window.clearTimeout(bannerId);
      window.clearTimeout(enterId);
    };
  }, [wave, status]);

  const persistResult = useCallback(
    async (finalScore, completed) => {
      if (savedRef.current || !apiClient) return;
      savedRef.current = true;
      try {
        const response = await apiClient.postGameProgress(
          "MATH_CASTLE_DEFENSE",
          Math.max(0, Math.round(finalScore)),
          completed,
          null
        );
        setStarsAwarded(Number(response?.stars_awarded || 0));
        window.dispatchEvent(new CustomEvent("games:progress-updated"));
        refreshUserData?.();
      } catch {
        // Progress saving should never interrupt a child's game result.
      }
    },
    [apiClient, refreshUserData]
  );

  const finishGame = useCallback(
    (didWin, finalScore = score) => {
      setWon(didWin);
      setStatus("result");
      setResolving(true);
      persistResult(finalScore, didWin);
    },
    [persistResult, score]
  );

  // A "Kingdom saved!" beat with confetti before the result card.
  const triggerVictory = useCallback(
    (finalScore) => {
      play("victory");
      setShowVictory(true);
      later(() => {
        setShowVictory(false);
        finishGame(true, finalScore);
      }, 1400);
    },
    [finishGame, later, play]
  );

  const loadEncounter = useCallback((nextWave) => {
    setWave(nextWave);
    setMonster(monsterForWave(nextWave));
    setProblem(generateCastleProblem(nextWave));
    setDistance(START_DISTANCE);
    setFeedback(null);
    setBoltFlight(false);
    setHitBurst(false);
    setMonsterHit(false);
    setMonsterDefeated(false);
    setBreachLunge(false);
    setAnswerFlash(null);
    setResolving(false);
  }, []);

  const completeEncounter = useCallback(
    (didDefeat, scoreAfter = score) => {
      if (wave >= TOTAL_WAVES) {
        later(() => triggerVictory(scoreAfter), 850);
        return;
      }
      later(() => loadEncounter(wave + 1), didDefeat ? 850 : 1050);
    },
    [later, loadEncounter, score, triggerVictory, wave]
  );

  const handleBreach = useCallback(() => {
    if (status !== "playing" || resolving) return;
    setResolving(true);
    setCastleShaking(true);
    setFieldShaking(true);
    setBreachLunge(true);
    setFeedback({ type: "breach", text: `${monster.name} bonked the gate!` });
    setCombo(0);
    play("breach");
    const nextHealth = castleHealth - 1;
    setCastleHealth(nextHealth);
    later(() => setCastleShaking(false), 650);
    later(() => setFieldShaking(false), 520);
    later(() => setBreachLunge(false), 420);
    if (nextHealth <= 0) {
      later(() => finishGame(false, score), 900);
    } else {
      completeEncounter(false, score);
    }
  }, [castleHealth, completeEncounter, finishGame, later, monster.name, play, resolving, score, status]);

  useEffect(() => {
    if (status !== "playing" || resolving || entering) return undefined;
    const rate = approachRateForWave(wave);
    const interval = window.setInterval(() => {
      setDistance((current) => Math.max(BREACH_DISTANCE - 1, current - rate));
    }, 100);
    return () => window.clearInterval(interval);
  }, [wave, resolving, status, entering]);

  useEffect(() => {
    if (distance <= BREACH_DISTANCE) handleBreach();
  }, [distance, handleBreach]);

  const startGame = useCallback(() => {
    timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutIdsRef.current.clear();
    savedRef.current = false;
    setStarsAwarded(0);
    setStatus("playing");
    setCastleHealth(MAX_CASTLE_HEALTH);
    setScore(0);
    setCombo(0);
    setBestCombo(0);
    setDefeated(0);
    setWon(false);
    setShowVictory(false);
    loadEncounter(1);
  }, [loadEncounter]);

  const handleAnswer = useCallback(
    (answer) => {
      if (status !== "playing" || resolving) return;
      setResolving(true);

      if (answer !== problem.answer) {
        setAnswerFlash({ value: answer, kind: "wrong" });
        setFeedback({ type: "wrong", text: "Almost! The creature scoots closer." });
        setCombo(0);
        setDistance((current) => Math.max(BREACH_DISTANCE - 1, current - 10));
        play("wrong");
        later(() => {
          setProblem(generateCastleProblem(wave));
          setFeedback(null);
          setAnswerFlash(null);
          setResolving(false);
        }, 720);
        return;
      }

      const nextMonsterHealth = healthAfterCorrectAnswer(monster.health);
      const nextCombo = combo + 1;
      const points = pointsForAnswer({ combo: nextCombo, distance });
      const nextScore = score + points;
      setScore(nextScore);
      setCombo(nextCombo);
      setBestCombo((current) => Math.max(current, nextCombo));
      setAnswerFlash({ value: answer, kind: "correct" });
      setFeedback({
        type: "correct",
        text: nextCombo >= 3 ? `${nextCombo}x combo! ${PRAISE[nextCombo % PRAISE.length]}` : PRAISE[nextCombo % PRAISE.length],
      });
      setScorePop(points);
      // Fire the arced star-bolt from the castle to the creature.
      setBoltFlight(true);
      play("correct");
      later(() => play("blast"), 100);
      // Impact!
      later(() => {
        setBoltFlight(false);
        setMonsterHit(true);
        setHitBurst(true);
      }, 330);
      later(() => setHitBurst(false), 830);
      later(() => setScorePop(null), 900);

      setMonster((current) => ({ ...current, health: nextMonsterHealth }));
      if (nextMonsterHealth <= 0) {
        setDefeated((current) => current + 1);
        later(() => setMonsterDefeated(true), 430);
        completeEncounter(true, nextScore);
      } else {
        later(() => {
          setProblem(generateCastleProblem(wave));
          setFeedback(null);
          setAnswerFlash(null);
          setMonsterHit(false);
          setResolving(false);
        }, 860);
      }
    },
    [combo, completeEncounter, distance, later, monster.health, play, problem.answer, resolving, score, status, wave]
  );

  useEffect(() => {
    const handleKey = (event) => {
      if (status !== "playing" || resolving) return;
      const index = Number(event.key) - 1;
      if (index >= 0 && index < problem.options.length) handleAnswer(problem.options[index]);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleAnswer, problem.options, resolving, status]);

  const progress = ((wave - 1) / TOTAL_WAVES) * 100;
  return (
    <main className="mcd-page">
      <ChallengeBanner target={challengeTarget} score={score} ended={status === "result"} />
      <div className="mcd-shell">
        <header className="mcd-topbar">
          <BackToGamesButton onClick={() => navigate("/games")} accent="#5d4cad" style={{ justifySelf: "start" }} />
          <div className="mcd-brand-lockup">
            <span className="mcd-brand-shield">♜</span>
            <div><small>Kingdom of Numbers</small><strong>Math Castle Defense</strong></div>
          </div>
          <button type="button" className="mcd-sound-button" onClick={toggleSound} aria-label={soundEnabled ? "Turn sound off" : "Turn sound on"}>
            {soundEnabled ? "♪" : "♪̸"}
          </button>
        </header>

        <section className={`mcd-game-frame ${danger ? "is-danger" : ""}`} aria-label="Math Castle Defense battlefield">
          <img
            className="mcd-bg"
            src={`${ASSET_BASE}/mc_bg.jpg`}
            alt=""
            aria-hidden="true"
            draggable="false"
            onError={(event) => event.currentTarget.classList.add("is-hidden")}
          />
          <div className="mcd-sky-glow" aria-hidden="true" />
          <div className="mcd-mountains" aria-hidden="true"><i /><i /><i /><i /></div>
          <div className="mcd-tree-line" aria-hidden="true" />
          <Clouds />
          <div className="mcd-danger-veil" aria-hidden="true" />

          <div className="mcd-hud">
            <div className="mcd-hud-card mcd-score-card"><span><StarIcon size={20} /></span><div><small>Score</small><strong>{score.toLocaleString()}</strong></div></div>
            <div className="mcd-wave-card">
              <div><span>Wave {wave}</span><small>of {TOTAL_WAVES}</small></div>
              <div className="mcd-wave-track"><i style={{ width: `${Math.min(100, progress)}%` }} /></div>
            </div>
            <div className={`mcd-hud-card mcd-castle-health-card ${atOneHeart ? "is-heartbeat" : ""}`}><span><img src={`${ASSET_BASE}/mc_castle.png`} alt="" draggable="false" style={{ height: 20, objectFit: "contain" }} /></span><div><small>Castle</small><HealthPips health={castleHealth} maxHealth={MAX_CASTLE_HEALTH} kind="castle" /></div></div>
          </div>

          {status === "playing" && (
            <>
              <div className="mcd-problem-banner">
                <span className="mcd-problem-label">{problem.operation} spell</span>
                <strong>{problem.prompt}</strong>
              </div>

              <div className={`mcd-battlefield ${fieldShaking ? "is-shaking" : ""}`}>
                <div className="mcd-castle-wrap">
                  <HealthPips health={castleHealth} maxHealth={MAX_CASTLE_HEALTH} kind="castle" />
                  <CastleFigure health={castleHealth} shaking={castleShaking} />
                </div>

                {boltFlight && (
                  <motion.div
                    className="mcd-starbolt"
                    aria-hidden="true"
                    initial={{ left: "15%", bottom: "40%", scale: 0.55, opacity: 0 }}
                    animate={{ left: `${distance}%`, bottom: ["40%", "64%", "32%"], scale: [0.55, 1, 1.08], opacity: [0, 1, 1] }}
                    transition={{ duration: 0.34, ease: "easeIn" }}
                  >
                    <span className="mcd-starbolt-core"><b>✦</b></span>
                  </motion.div>
                )}

                <div className="mcd-monster-wrap" style={{ left: `${distance}%` }}>
                  <motion.div
                    className="mcd-monster-inner"
                    key={`monster-${wave}`}
                    initial={{ x: 260, opacity: 0 }}
                    animate={{ x: breachLunge ? -34 : 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 260, damping: 20 }}
                  >
                    {danger && !monsterDefeated && <div className="mcd-danger-ring" aria-hidden="true" />}
                    <div className="mcd-monster-name"><strong>{monster.name}</strong><HealthPips health={monster.health} maxHealth={monster.maxHealth} /></div>
                    <MonsterFigure monster={monster} hit={monsterHit} defeated={monsterDefeated} />
                    {hitBurst && (
                      <div className="mcd-impact" aria-hidden="true">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <i key={i} style={{ "--a": `${i * 45}deg` }} />
                        ))}
                      </div>
                    )}
                    {scorePop != null && <div className="mcd-score-pop">+{scorePop}</div>}
                  </motion.div>
                </div>

                {feedback && <div className={`mcd-feedback is-${feedback.type}`}>{feedback.text}</div>}
                {showWaveBanner && (
                  <div className="mcd-wave-banner" key={wave}>
                    <small>Wave {wave} of {TOTAL_WAVES}</small>
                    <strong>Here comes {monster.name}!</strong>
                  </div>
                )}

                <AnimatePresence>
                  {showVictory && (
                    <motion.div
                      className="mcd-victory-banner"
                      initial={{ opacity: 0, scale: 0.5, rotate: -6, x: "-50%" }}
                      animate={{ opacity: 1, scale: 1, rotate: 0, x: "-50%" }}
                      exit={{ opacity: 0, scale: 0.9, x: "-50%" }}
                      transition={{ type: "spring", stiffness: 300, damping: 16 }}
                    >
                      <span><CrownIcon size={30} /></span><strong>Kingdom saved!</strong>
                    </motion.div>
                  )}
                </AnimatePresence>
                {showVictory && (
                  <div className="mcd-confetti" aria-hidden="true">
                    {Array.from({ length: 26 }).map((_, i) => (
                      <i key={i} style={{ "--i": i }} />
                    ))}
                  </div>
                )}
              </div>

              <div className="mcd-control-deck">
                <div className="mcd-answer-panel">
                  <div className="mcd-answer-grid">
                    {problem.options.map((option, index) => {
                      const flash = answerFlash?.value === option ? answerFlash.kind : null;
                      const dimmed = resolving && answerFlash != null && answerFlash.value !== option;
                      return (
                        <button
                          type="button"
                          key={`${problem.prompt}-${option}`}
                          className={`${flash ? `is-${flash}` : ""} ${dimmed ? "is-dimmed" : ""}`}
                          onClick={() => handleAnswer(option)}
                          disabled={resolving}
                        >
                          <kbd>{index + 1}</kbd><strong>{option}</strong>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mcd-combo-row">
                    <span className={combo >= 3 ? "is-hot" : ""}><FlameIcon size={14} /> {combo > 1 ? `${combo} answer streak!` : "Build a streak for bonus points"}</span>
                    <small><BoltIcon size={12} /> Waves get faster</small>
                  </div>
                </div>
              </div>
            </>
          )}

          {status === "intro" && (
            <IntroOverlay onStart={startGame} onBack={() => navigate("/games")} />
          )}
          {status === "result" && (
            <ResultOverlay won={won} score={score} defeated={defeated} bestCombo={bestCombo} starsAwarded={starsAwarded} onRetry={startGame} onBack={() => navigate("/games")} />
          )}
        </section>
      </div>
    </main>
  );
};

export default MathCastleDefensePage;
